import { OrganizationsService, SubscriptionService, useApiEffect, UserService } from '@localstack/services';
import { PlanFamily, Subscription } from '@localstack/types';
import { useCallback } from 'react';

/** For a subscription to be switchable a few criteria must be met:
 * - It must not be in a cancelling state as we currently don't support converting cancelling subs.
 * - The plan must be switchable.
 * - A trial should be switchable (it is always cancelling by default, so it is the exception to the cancelling rule above).
 */
export const isSubscriptionSwitchEligible = (subscription: Subscription): boolean => {
  const isNotCancelling = !(subscription.cancel_at_period_end || subscription.cancel_at);
  const isTrial = subscription.plan?.family === PlanFamily.TRIAL_PLANS;
  const isSwitchable = !!subscription.plan.switchable;

  return (isNotCancelling || isTrial) && isSwitchable;
};

type SubscribeDirectlyArgs = {
  planId: string;
  /** When present and switch-eligible, the plan is switched onto this sub instead of creating a new one. */
  existingSubscription?: Subscription;
  seats?: number;
};

type UseDirectSubscribeReturn = {
  isLoading: boolean;
  /**
   * Subscribe to a plan without routing through the checkout page. Resolves with the resulting
   * subscription, or `undefined` when the call failed (the API layer swallows the error and
   * surfaces it through the error snackbar), so callers must check before acting on success.
   */
  subscribeDirectly: (args: SubscribeDirectlyArgs) => Promise<Subscription | undefined>;
};

/**
 * Core "subscribe without checkout" primitive shared by the zero-payment flows (Hobby, Trial).
 * Owns the create-vs-update decision and the subscription cache revalidation.
 */
export const useDirectSubscribe = (): UseDirectSubscribeReturn => {
  const { createSubscription, updateSubscription, isLoading } = useApiEffect(
    SubscriptionService,
    ['createSubscription', 'updateSubscription'],
    {
      revalidate: [
        'listSubscriptions',
        'getSubscription',
        'listInvoices',
        'listPlans',
        'getAggregatedSubscriptionInfo',
      ],
      revalidateOtherClient: {
        client: [OrganizationsService, UserService],
        methods: ['listLicenseAssignments', 'listLicenseAssignmentsForUser', 'getUser'],
      },
    },
  );

  const subscribeDirectly = useCallback(
    async ({ planId, existingSubscription, seats = 1 }: SubscribeDirectlyArgs): Promise<Subscription | undefined> => {
      const shouldUpdateExisting = existingSubscription && isSubscriptionSwitchEligible(existingSubscription);

      return shouldUpdateExisting
        ? updateSubscription(existingSubscription.id, { plan: planId, seats })
        : createSubscription({ plan: planId, seats });
    },
    [createSubscription, updateSubscription],
  );

  return { isLoading, subscribeDirectly };
};
